import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {
    APIProvider,
    Map,
    AdvancedMarker,
    useMap,
    type MapCameraChangedEvent,
    type MapMouseEvent,
} from '@vis.gl/react-google-maps'
import {
    BedDouble,
    Briefcase,
    ChevronDown,
    Coffee,
    Dumbbell,
    House,
    Plane,
    ShoppingBag,
    Tag,
    Trees,
    Utensils,
    Wine,
    type LucideIcon,
} from 'lucide-react'
import { api, putDeviceLocation } from '../../api/client'
import { StandardPin } from './pinStyle'

/** Category-slug → Lucide icon. Slugs come from `place_categories.slug`
 *  in the DB; this mapping is the visual layer for the icon-tile picker.
 *  Unknown slugs fall through to a generic `Tag` icon. */
const CATEGORY_ICON: Record<string, LucideIcon> = {
    home: House,
    work: Briefcase,
    restaurant: Utensils,
    cafe: Coffee,
    bar: Wine,
    gym: Dumbbell,
    park: Trees,
    shop: ShoppingBag,
    hotel: BedDouble,
    travel: Plane,
    other: Tag,
}

/**
 * Modal location picker. Backed by Google Maps JS via @vis.gl/react-google-maps,
 * with our own typeahead (calls /api/places/resolve_search). The picker
 * produces one of two result kinds:
 *
 *  - `kind: 'place'` — user picked a typeahead candidate. The picker calls
 *    /api/places/find_or_create on confirm to materialize the Places item;
 *    the caller stores `collectionItemId` as a normal `reference_value`.
 *  - `kind: 'coords'` — user dropped/dragged a pin without resolving to a
 *    place. The caller stores `(latitude, longitude)` directly.
 *
 * Default-center cascade on open (highest priority first):
 *   1. per-session sticky pan (module-level — survives consecutive opens
 *      until the tab closes; resets on "use my current location")
 *   2. `initial` prop, if editing an existing row
 *   3. server-stored `users.last_map_center_*`
 *   4. `navigator.geolocation.getCurrentPosition` (HTTPS only; user-gated)
 *   5. server-side IP fallback (POST /api/places/geolocate)
 *   6. wide world view (lat 0, lng 0, zoom 2)
 */

const GOOGLE_MAPS_API_KEY = import.meta.env.VITE_GOOGLE_MAPS_BROWSER_KEY as string | undefined
const PICKER_MAP_ID = 'memgenie-location-picker'

const DEVICE_GEO_TIMEOUT_MS = 6000
const TYPEAHEAD_DEBOUNCE_MS = 250
const TYPEAHEAD_MIN_CHARS = 2

const FALLBACK_VIEW = { lat: 0, lng: 0, zoom: 2 } as const
const DEVICE_GEO_ZOOM = 14
const PLACE_PICK_ZOOM = 16
const IP_GEO_ZOOM = 10

// Per-session sticky pan/zoom. Module-level on purpose — survives consecutive
// modal opens within the same tab so editing a few checkins about the same
// event cluster keeps the user's view. "Use my current location" resets it.
let sessionStickyView: { lat: number; lng: number; zoom: number } | null = null

interface PlaceCandidate {
    /** 'private' candidates already live in the user's Places collection
     *  (instant pick — no /find_or_create round-trip). 'google' candidates
     *  are Text Search hits the picker materializes on confirm. */
    kind: 'private' | 'google'
    /** Set when kind='private'; used by handleConfirm to skip find_or_create. */
    collection_item_id?: number | null
    google_place_id?: string | null
    display_name?: string | null
    formatted_address?: string | null
    lat?: number | null
    lng?: number | null
    types?: string[]
}

export type LocationPickerInitial =
    | {
        kind: 'place'
        collectionItemId: number
        lat?: number | null
        lng?: number | null
        name?: string | null
        /** Only used in edit mode (PlacesCollectionPage). Pre-selects the
         *  category in the dropdown. */
        categoryId?: number | null
        /** Only used in edit mode. Seeds the address typeahead with the
         *  row's existing free-text address so the user sees what's there
         *  without having to retype it. The seed is treated as text only
         *  — leaving it untouched at save time means the address stays
         *  exactly as it was. */
        existingAddress?: string | null
        /** Only used in edit mode. Seeds the freeform notes textarea. */
        notes?: string | null
    }
    | { kind: 'coords'; latitude: number; longitude: number }

export type LocationPickerResult =
    | {
        kind: 'place'
        collectionItemId: number
        lat?: number
        lng?: number
        name?: string
    }
    | { kind: 'coords'; latitude: number; longitude: number }

/** Payload handed to `onEditSave` when the picker is in edit-existing-place
 *  mode. Unlike the find_or_create path, this carries the user's intent
 *  raw — the caller does the PUT against the Places collection_item.
 *  `categoryId` is only meaningful for private places; the picker hides
 *  the dropdown for Google-backed Public places (they're not editable
 *  in this UI). */
export interface PlaceEditPayload {
    name: string
    lat: number
    lng: number
    googlePlaceId: string | null
    formattedAddress: string | null
    categoryId: number | null
    notes: string | null
}

interface LocationPickerModalProps {
    open: boolean
    initial?: LocationPickerInitial | null
    /** Modal title. Defaults to "Add Location" / "Edit Location" based on
     *  whether `initial` is provided. Override for non-composer contexts
     *  (e.g., the Places collection editor uses "Add Place" / "Edit Place"). */
    title?: string
    /** Confirm-button label. Defaults to "Add" / "Save" based on `initial`. */
    confirmLabel?: string
    onConfirm: (result: LocationPickerResult) => void
    /** Fires after /find_or_create returns a Places item id+data — both for
     *  freshly-created rows and for "already existed" hits. The caller wires
     *  this to `mergeCollectionItems` so the page-level cache picks up the
     *  new item; without it, list rows render as "Place #N" until reload. */
    onCollectionItemReady?: (item: { id: number; data: Record<string, any> }) => void
    /** When set, the picker enters edit-existing-place mode:
     *   - The mode toggle is hidden; only the create form (name + pin) is
     *     shown. Search-mode editing isn't a meaningful operation here
     *     (would mean "switch this place row to a different one") and
     *     pin-only repinning is folded into create with a blank name.
     *   - On confirm, instead of /find_or_create, calls `onEditSave` with
     *     the merged fields. The caller is responsible for PUTting against
     *     the Places collection item. `onConfirm` still fires after a
     *     successful save so the parent can refresh / close in one shot. */
    editingPlaceId?: number
    onEditSave?: (fields: PlaceEditPayload) => Promise<void>
    /** When true, locks the picker to 'create' mode and hides the mode
     *  toggle. Used by PlacesCollectionPage's "Add place" entry point,
     *  where Search (already-saved places) and Pin (bare coords, no Place
     *  row) aren't meaningful operations. */
    lockedToCreate?: boolean
    /** User-named places already in this user's Places collection. Only
     *  meaningful with `lockedToCreate`: the picker compares `createName`
     *  against this list (case-insensitive trim) and surfaces a plain
     *  inline "already in your places" warning + disables Add when a
     *  collision is detected, so the silent server-side dedup doesn't
     *  swallow the user's intent without them noticing. */
    existingPrivateNames?: string[]
    onClose: () => void
}

/** Three interaction modes the user can switch between via the segmented
 *  toggle at the top of the modal:
 *
 *    'search' — pick something that already exists (private place or vanilla
 *               Google place). No naming, no pin drop. The map is read-only
 *               context for the selection.
 *    'create' — name + (address typeahead OR pin drop). The "last action
 *               wins" for the location: picking an address sets pin AND
 *               google_place_id; clicking/dragging the map updates pin and
 *               clears google_place_id (the pin is no longer the Google
 *               place). The name field is prominent and editable; renaming
 *               a Google-resolved address downgrades the save to a private
 *               place with no google_place_id (your name beats theirs).
 *    'pin'    — drop a pin and confirm. No name, no address, no Google
 *               linkage. Same map plumbing as 'create'; the only difference
 *               is the inputs are hidden and confirm always saves bare
 *               coords. Useful when the user just wants to mark a spot.
 */
type PickerMode = 'search' | 'create' | 'pin'

const LocationPickerModal: React.FC<LocationPickerModalProps> = ({
    open,
    initial,
    title,
    confirmLabel,
    onConfirm,
    onCollectionItemReady,
    editingPlaceId,
    onEditSave,
    lockedToCreate,
    existingPrivateNames,
    onClose,
}) => {
    if (!open) return null

    if (!GOOGLE_MAPS_API_KEY) {
        // Dev-time guardrail: render a clear error rather than a blank map
        // so it's obvious what to fix. Browser-restricted key is safe to
        // expose; missing it means env wasn't picked up.
        return (
            <Backdrop onClose={onClose}>
                <div className="bg-white rounded-lg shadow-xl p-6 max-w-md">
                    <h3 className="text-lg font-medium text-gray-900 mb-2">Location picker unavailable</h3>
                    <p className="text-sm text-gray-600">
                        VITE_GOOGLE_MAPS_BROWSER_KEY is not set. Restart the dev server after
                        adding it to <code>frontend/.env.development</code>.
                    </p>
                </div>
            </Backdrop>
        )
    }

    const editing = editingPlaceId != null
    const resolvedTitle =
        title ?? (editing
            ? 'Edit Place'
            : lockedToCreate
                ? 'Add Place'
                : initial
                    ? 'Edit Location'
                    : 'Add Location')
    const resolvedConfirmLabel = confirmLabel ?? (initial || editing ? 'Save' : 'Add')

    return (
        <Backdrop onClose={onClose}>
            <div
                className="bg-white rounded-lg shadow-xl flex flex-col w-[720px] max-w-[95vw] h-[560px] max-h-[90vh] overflow-hidden"
                onMouseDown={(e) => e.stopPropagation()}
                onClick={(e) => e.stopPropagation()}
            >
                <APIProvider apiKey={GOOGLE_MAPS_API_KEY}>
                    <PickerBody
                        initial={initial ?? null}
                        title={resolvedTitle}
                        confirmLabel={resolvedConfirmLabel}
                        onConfirm={onConfirm}
                        onCollectionItemReady={onCollectionItemReady}
                        editingPlaceId={editingPlaceId}
                        onEditSave={onEditSave}
                        lockedToCreate={lockedToCreate}
                        existingPrivateNames={existingPrivateNames}
                        onClose={onClose}
                    />
                </APIProvider>
            </div>
        </Backdrop>
    )
}

const Backdrop: React.FC<{ onClose: () => void; children: React.ReactNode }> = ({
    onClose,
    children,
}) => (
    // Stop mousedown at the backdrop so the underlying ItemListEditorShell's
    // document-level click-outside listener (which fires on mousedown, not
    // click) doesn't treat picker interactions as an outside-click and
    // prematurely save the location list. Click-to-dismiss-on-backdrop still
    // works because `onClick` is a separate event.
    <div
        className="fixed inset-0 bg-black/40 flex items-center justify-center z-[1000]"
        onMouseDown={(e) => e.stopPropagation()}
        onClick={onClose}
    >
        {children}
    </div>
)

const PickerBody: React.FC<{
    initial: LocationPickerInitial | null
    title: string
    confirmLabel: string
    onConfirm: (result: LocationPickerResult) => void
    onCollectionItemReady?: (item: { id: number; data: Record<string, any> }) => void
    editingPlaceId?: number
    onEditSave?: (fields: PlaceEditPayload) => Promise<void>
    lockedToCreate?: boolean
    existingPrivateNames?: string[]
    onClose: () => void
}> = ({ initial, title, confirmLabel, onConfirm, onCollectionItemReady, editingPlaceId, onEditSave, lockedToCreate, existingPrivateNames, onClose }) => {
    const editing = editingPlaceId != null
    // ── Map view (uncontrolled; programmatic recentering only) ──────────
    // initialView is computed once and handed to <Map> as defaultCenter/
    // defaultZoom. Controlled center/zoom would snap the map back to React
    // state on every render and break user gestures. All programmatic
    // moves go through `pendingRecenter` → MapController.
    const initialView = useMemo<{ lat: number; lng: number; zoom: number }>(() => {
        if (sessionStickyView) {
            return { lat: sessionStickyView.lat, lng: sessionStickyView.lng, zoom: sessionStickyView.zoom }
        }
        if (initial?.kind === 'coords') {
            return { lat: initial.latitude, lng: initial.longitude, zoom: PLACE_PICK_ZOOM }
        }
        if (initial?.kind === 'place' && initial.lat != null && initial.lng != null) {
            return { lat: initial.lat, lng: initial.lng, zoom: PLACE_PICK_ZOOM }
        }
        return { lat: FALLBACK_VIEW.lat, lng: FALLBACK_VIEW.lng, zoom: FALLBACK_VIEW.zoom }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    const [pendingRecenter, setPendingRecenter] = useState<
        { lat: number; lng: number; zoom: number } | null
    >(null)
    // Live map view, kept in sync via onCameraChanged. Used by handleConfirm
    // to PUT the user's last_map_center without reading from React state.
    const liveViewRef = useRef<{ lat: number; lng: number; zoom: number }>(initialView)

    // ── Mode + per-mode state ───────────────────────────────────────────
    // Default mode:
    //   - editing a coords-only row → 'pin' (no name to keep; only the
    //     pin moves)
    //   - editing a place-ref row → 'create' (so the user can rename /
    //     move the pin / confirm unchanged)
    //   - fresh add → 'search' (most common case is attaching something
    //     that already exists)
    const [mode, setMode] = useState<PickerMode>(() => {
        // Edit-existing-place mode: lock to 'create' so the user can rename
        // and/or repin. Switching to 'search' would mean "make this row
        // point at a different Place," which we don't support — that's a
        // delete + re-add, not an edit.
        if (editingPlaceId != null) return 'create'
        // Locked-to-create (e.g. PlacesCollectionPage's "Add place"): the
        // user explicitly wants a brand-new Place row, so search (would just
        // surface things they already have) and pin (bare coords, no row)
        // are off the table.
        if (lockedToCreate) return 'create'
        if (initial?.kind === 'coords') return 'pin'
        if (initial?.kind === 'place') return 'create'
        return 'search'
    })

    // Mode 1 (search): one selected candidate, that's it.
    const [searchPicked, setSearchPicked] = useState<{
        candidate: PlaceCandidate
        existingCollectionItemId?: number
    } | null>(null)

    // Mode 2 (create): name + pin. The address typeahead is a coords lookup
    // and a way to capture a formal address string; it never links the saved
    // Place to a Google entity. The "New" tab is always Mine — picking a
    // Google address from search is what the "Existing place" tab is for.
    const [createName, setCreateName] = useState<string>(() => {
        if (initial?.kind === 'place') return initial.name ?? ''
        return ''
    })
    const [createPin, setCreatePin] = useState<{ lat: number; lng: number } | null>(() => {
        if (initial?.kind === 'coords') return { lat: initial.latitude, lng: initial.longitude }
        if (initial?.kind === 'place' && initial.lat != null && initial.lng != null) {
            return { lat: initial.lat, lng: initial.lng }
        }
        return null
    })
    // Edit-mode-only: category id picked from the dropdown. Initialized
    // from `initial.categoryId` so the existing value pre-selects.
    const [editCategoryId, setEditCategoryId] = useState<number | null>(() => {
        if (initial?.kind === 'place' && initial.categoryId != null) {
            return initial.categoryId
        }
        return null
    })
    // List of categories fetched lazily on first render. The static set is
    // small (11), so cache once and skip re-fetching. `slug` is kept so the
    // tile grid can map to the right icon. Fetched in both edit mode and
    // lockedToCreate mode (Add Place form needs the picker too).
    const [categories, setCategories] = useState<
        { id: number; name: string; slug: string }[]
    >([])
    useEffect(() => {
        if (editingPlaceId == null && !lockedToCreate) return
        let cancelled = false
        api.get<{ categories: { id: number; name: string; slug: string }[] }>(
            '/api/places/categories'
        )
            .then((resp) => {
                if (cancelled) return
                // Pin "Other" to the bottom; the API sorts alphabetically,
                // which buries it mid-list ("Hotel, Other, Park, ..."). It
                // reads better as the escape hatch — visually clearly after
                // the substantive categories.
                const sorted = [...resp.data.categories].sort((a, b) => {
                    if (a.slug === 'other') return 1
                    if (b.slug === 'other') return -1
                    return 0
                })
                setCategories(sorted)
            })
            .catch((err) => console.error('Failed to load place categories:', err))
        return () => { cancelled = true }
    }, [editingPlaceId, lockedToCreate])


    // Formal address from the typeahead pick. Persisted on save; cleared
    // when the user repositions the pin (the address no longer matches).
    const [createFormattedAddress, setCreateFormattedAddress] = useState<string | null>(null)

    // Edit-mode-only: the address textbox's current contents. Seeded from
    // `initial.existingAddress` so the user sees what's there. Tracks whether
    // they typed/picked/cleared. Saved as `formattedAddress` on confirm; the
    // parent's merge falls back to the existing value when this is null.
    const [editAddress, setEditAddress] = useState<string>(() => {
        if (editingPlaceId != null && initial?.kind === 'place') {
            return initial.existingAddress ?? ''
        }
        return ''
    })

    // Edit-mode-only: freeform notes (e.g. "Coachella 2026") to tell similar
    // places apart in the grid. Seeded from `initial.notes`.
    const [editNotes, setEditNotes] = useState<string>(() => {
        if (editingPlaceId != null && initial?.kind === 'place') {
            return initial.notes ?? ''
        }
        return ''
    })

    const [confirming, setConfirming] = useState(false)

    // ── Default-center cascade (run once when no initial / sticky view) ─
    const cascadeRan = useRef(false)
    useEffect(() => {
        if (cascadeRan.current) return
        cascadeRan.current = true
        if (sessionStickyView || initial) return

        let cancelled = false
        ;(async () => {
            // 3. server-stored last_map_center
            try {
                const resp = await api.get('/api/places/me/last_map_center')
                const { lat, lng, zoom: z } = resp.data || {}
                if (!cancelled && lat != null && lng != null) {
                    setPendingRecenter({ lat, lng, zoom: z ?? PLACE_PICK_ZOOM })
                    return
                }
            } catch { /* fall through */ }

            // 4. device geolocation (gated on permission, HTTPS only)
            const deviceCoords = await tryDeviceGeolocation()
            if (!cancelled && deviceCoords) {
                setPendingRecenter({ ...deviceCoords, zoom: DEVICE_GEO_ZOOM })
                return
            }

            // 5. server IP fallback
            try {
                const resp = await api.post('/api/places/geolocate')
                const { lat, lng, source } = resp.data || {}
                if (!cancelled && lat != null && lng != null && source !== 'fallback') {
                    setPendingRecenter({ lat, lng, zoom: IP_GEO_ZOOM })
                    return
                }
            } catch { /* fall through */ }

            // 6. wide world view — already covered by initializer.
        })()

        return () => { cancelled = true }
    }, [initial])

    // ── Map plumbing ────────────────────────────────────────────────────
    const handleCameraChanged = useCallback((ev: MapCameraChangedEvent) => {
        const c = ev.detail?.center
        const z = ev.detail?.zoom
        if (!c || z == null) return
        const view = { lat: c.lat, lng: c.lng, zoom: z }
        sessionStickyView = view
        liveViewRef.current = view
    }, [])

    const handleMapClick = useCallback((e: MapMouseEvent) => {
        const ll = e.detail?.latLng
        if (!ll) return
        // Map clicks drop pins in 'create' and 'pin' modes only. In 'search'
        // the map is read-only context — we don't want to surprise-switch the
        // user into create/pin just because they tapped the map.
        if (mode !== 'create' && mode !== 'pin') return
        setCreatePin({ lat: ll.lat, lng: ll.lng })
        setCreateFormattedAddress(null) // pin moved; typed address no longer matches
    }, [mode])

    const handleMarkerDrag = useCallback((lat: number, lng: number) => {
        // Drag is allowed in 'create' and 'pin' (search marker is non-draggable).
        // Dragging clears the formal address (the pin no longer points at it);
        // the user's typed name is left intact.
        setCreatePin({ lat, lng })
        setCreateFormattedAddress(null)
    }, [])

    const handleUseCurrentLocation = useCallback(async () => {
        sessionStickyView = null
        const deviceCoords = await tryDeviceGeolocation()
        if (deviceCoords) {
            setPendingRecenter({ ...deviceCoords, zoom: DEVICE_GEO_ZOOM })
            return
        }
        try {
            const resp = await api.post('/api/places/geolocate')
            const { lat, lng } = resp.data || {}
            if (lat != null && lng != null) {
                setPendingRecenter({ lat, lng, zoom: IP_GEO_ZOOM })
            }
        } catch { /* swallow */ }
    }, [])

    // ── Mode 1 (search) typeahead callback ──────────────────────────────
    const handleSearchSelect = useCallback((candidate: PlaceCandidate) => {
        setSearchPicked({
            candidate,
            existingCollectionItemId:
                candidate.kind === 'private' && candidate.collection_item_id != null
                    ? candidate.collection_item_id
                    : undefined,
        })
        if (candidate.lat != null && candidate.lng != null) {
            setPendingRecenter({ lat: candidate.lat, lng: candidate.lng, zoom: PLACE_PICK_ZOOM })
        }
    }, [])

    // ── Mode 2 (create) address typeahead callback ──────────────────────
    const handleCreateAddressSelect = useCallback((candidate: PlaceCandidate) => {
        // The address pick is a coords + address lookup, nothing more — the
        // saved Place stays Mine regardless. Don't auto-fill the name (the
        // user must type one themselves) and don't track the gpid.
        if (candidate.lat != null && candidate.lng != null) {
            setCreatePin({ lat: candidate.lat, lng: candidate.lng })
            setPendingRecenter({ lat: candidate.lat, lng: candidate.lng, zoom: PLACE_PICK_ZOOM })
        }
        if (editing) {
            // The TypeaheadInput's own select() handler updates the displayed
            // text via onTextChange, which already flows back into `editAddress`.
            return
        }
        setCreateFormattedAddress(candidate.formatted_address ?? null)
    }, [editing])

    // ── Marker derived state ────────────────────────────────────────────
    const markerPosition = useMemo<{ lat: number; lng: number } | null>(() => {
        if (mode === 'search') {
            const c = searchPicked?.candidate
            if (c?.lat != null && c?.lng != null) return { lat: c.lat, lng: c.lng }
            return null
        }
        // 'create' and 'pin' both use the createPin state.
        return createPin
    }, [mode, searchPicked, createPin])

    // lockedToCreate-only: collision check against the user's existing
    // user-named Places. Case-insensitive trim. Server still dedups on
    // commit; this is the UI signal so the dedup doesn't silently swallow
    // the user's intent.
    const nameCollision = useMemo(() => {
        if (!lockedToCreate) return false
        const trimmed = createName.trim().toLowerCase()
        if (!trimmed) return false
        return (existingPrivateNames ?? []).some(
            (n) => n.trim().toLowerCase() === trimmed
        )
    }, [lockedToCreate, createName, existingPrivateNames])

    // lockedToCreate-only: the in-form category picker. Required because the
    // "New" tab always saves Mine, and Mine rows need a deliberate category
    // to avoid landing at NULL.
    const categoryRequired = !!lockedToCreate

    // ── Confirm logic ───────────────────────────────────────────────────
    // 'create' requires a pin AND a user-typed name. A bare pin with no name
    // belongs in the 'pin' tab.
    const canConfirm =
        mode === 'search' ? !!searchPicked
        : mode === 'pin' ? !!createPin
        : (
            !!createPin
            && createName.trim().length > 0
            && !nameCollision
            && (!categoryRequired || editCategoryId != null)
        )
    const handleConfirm = async () => {
        if (!canConfirm || confirming) return
        setConfirming(true)
        try {
            // Roll the user's new map default forward (non-blocking).
            try {
                const v = liveViewRef.current
                await api.put('/api/places/me/last_map_center', {
                    lat: v.lat,
                    lng: v.lng,
                    zoom: v.zoom,
                })
            } catch { /* non-blocking */ }

            // Edit-existing-place: hand the raw fields to the parent and
            // let it PUT against the collection_item. Skip find_or_create
            // entirely — that path always creates or returns an existing
            // row, never updates one.
            if (editing && onEditSave) {
                const pin = createPin!
                const name = createName.trim()
                // Edit mode never links to a Google entity, even if the user
                // picked a Google address from the typeahead — that's a
                // pin/address fix, not a "convert this place to a Google
                // entity" operation. Strip the gpid; keep just the address
                // text for `formattedAddress`.
                await onEditSave({
                    name,
                    lat: pin.lat,
                    lng: pin.lng,
                    googlePlaceId: null,
                    formattedAddress: editAddress.trim() || null,
                    categoryId: editCategoryId,
                    notes: editNotes.trim() || null,
                })
                // Echo a result for parents that wire onConfirm too (e.g.,
                // for closing or focus restoration). The id is the existing
                // place id since this was an in-place update.
                onConfirm({
                    kind: 'place',
                    collectionItemId: editingPlaceId!,
                    lat: pin.lat,
                    lng: pin.lng,
                    name,
                })
                return
            }

            if (mode === 'search') {
                const sp = searchPicked!
                if (sp.existingCollectionItemId) {
                    onConfirm({
                        kind: 'place',
                        collectionItemId: sp.existingCollectionItemId,
                        lat: sp.candidate.lat ?? undefined,
                        lng: sp.candidate.lng ?? undefined,
                        name: sp.candidate.display_name ?? undefined,
                    })
                    return
                }
                // Google candidate, never picked before — materialize it.
                const resp = await api.post('/api/places/find_or_create', {
                    google_place_id: sp.candidate.google_place_id,
                    display_name: sp.candidate.display_name,
                    formatted_address: sp.candidate.formatted_address,
                    lat: sp.candidate.lat,
                    lng: sp.candidate.lng,
                    types: sp.candidate.types ?? [],
                })
                const id = resp.data?.collection_item_id
                if (typeof id !== 'number') throw new Error('find_or_create returned no id')
                if (onCollectionItemReady && resp.data?.data) {
                    onCollectionItemReady({ id, data: resp.data.data })
                }
                onConfirm({
                    kind: 'place',
                    collectionItemId: id,
                    lat: sp.candidate.lat ?? undefined,
                    lng: sp.candidate.lng ?? undefined,
                    name: sp.candidate.display_name ?? undefined,
                })
                return
            }

            if (mode === 'pin') {
                // Pin mode: bare coords, no name. The mode toggle hides the
                // name/address inputs, so any stale createName is ignored.
                const pin = createPin!
                onConfirm({ kind: 'coords', latitude: pin.lat, longitude: pin.lng })
                return
            }

            // mode === 'create' — always Mine. canConfirm enforces a typed
            // name; the backend's `creation_source = picker_user_named` keeps
            // the row sticky against orphan GC. `category_id` is sent only on
            // the lockedToCreate (Add Place) path; the checkin composer path
            // leaves it null.
            const pin = createPin!
            const name = createName.trim()
            const resp = await api.post('/api/places/find_or_create', {
                display_name: name,
                formatted_address: createFormattedAddress ?? undefined,
                lat: pin.lat,
                lng: pin.lng,
                category_id: categoryRequired ? editCategoryId : undefined,
            })
            const id = resp.data?.collection_item_id
            if (typeof id !== 'number') throw new Error('find_or_create returned no id')
            if (onCollectionItemReady && resp.data?.data) {
                onCollectionItemReady({ id, data: resp.data.data })
            }
            onConfirm({
                kind: 'place',
                collectionItemId: id,
                lat: pin.lat,
                lng: pin.lng,
                name,
            })
        } finally {
            setConfirming(false)
        }
    }

    // ── Status text below the map ───────────────────────────────────────
    const statusText: React.ReactNode = (() => {
        if (mode === 'search') {
            if (!searchPicked) return ''
            const c = searchPicked.candidate
            return (
                <span>
                    <span className="font-medium text-gray-700">{c.display_name}</span>
                    {c.formatted_address ? ` — ${c.formatted_address}` : ''}
                </span>
            )
        }
        if (!createPin) {
            return mode === 'pin'
                ? 'Click the map to drop a pin.'
                : 'Type an address or click the map to drop a pin.'
        }
        return (
            <span className="font-medium text-gray-700">
                {createPin.lat.toFixed(5)}°, {createPin.lng.toFixed(5)}°
            </span>
        )
    })()

    return (
        <>
            <div className="px-4 py-3 border-b border-gray-200 flex items-center justify-between">
                <h3 className="text-sm font-medium text-gray-900">{title}</h3>
                <button
                    onClick={onClose}
                    className="text-gray-400 hover:text-gray-600"
                    aria-label="Close"
                >
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                </button>
            </div>

            {/* Mode toggle — hidden in edit-existing-place mode (locked to
                'create' since the other modes don't represent editing) and
                in locked-to-create mode (the "Add place" entry point). */}
            {!editing && !lockedToCreate && (
                <div className="px-4 pt-3">
                    <div className="inline-flex rounded-md border border-gray-300 overflow-hidden text-sm">
                        <button
                            type="button"
                            onClick={() => setMode('search')}
                            className={`px-3 py-1.5 ${mode === 'search' ? 'bg-action text-plum' : 'bg-white text-gray-700 hover:bg-gray-50'}`}
                        >
                            Existing place
                        </button>
                        <button
                            type="button"
                            onClick={() => setMode('create')}
                            className={`px-3 py-1.5 border-l border-gray-300 ${mode === 'create' ? 'bg-action text-plum' : 'bg-white text-gray-700 hover:bg-gray-50'}`}
                        >
                            New
                        </button>
                        <button
                            type="button"
                            onClick={() => setMode('pin')}
                            className={`px-3 py-1.5 border-l border-gray-300 ${mode === 'pin' ? 'bg-action text-plum' : 'bg-white text-gray-700 hover:bg-gray-50'}`}
                        >
                            Pin
                        </button>
                    </div>
                </div>
            )}

            {/* Mode-specific body */}
            {mode === 'search' && (
                <div className="px-4 py-3">
                    <div className="flex gap-2">
                        <TypeaheadInput
                            onSelect={handleSearchSelect}
                            placeholder="Search for a place or address…"
                        />
                        <button
                            onClick={handleUseCurrentLocation}
                            className="px-3 py-2 text-sm text-amber-800 border border-amber-200 rounded hover:bg-amber-50 whitespace-nowrap"
                            title="Use my current location"
                        >
                            📍 Me
                        </button>
                    </div>
                </div>
            )}
            {mode === 'create' && (
                <div className="px-4 py-3 space-y-3">
                    <div>
                        <label className="block text-xs font-medium text-gray-700 mb-1">Name</label>
                        <input
                            type="text"
                            value={createName}
                            onChange={(e) => setCreateName(e.target.value)}
                            placeholder="e.g. Home or Johnny Smith's"
                            className={`w-full px-3 py-2 text-sm border rounded focus:outline-none focus:ring-2 ${nameCollision
                                ? 'border-rose-300 focus:ring-rose-500'
                                : 'border-gray-300 focus:ring-action'
                                }`}
                        />
                        {nameCollision && (
                            <p className="mt-1 text-xs text-rose-600">
                                Already in your places. Pick a different name.
                            </p>
                        )}
                    </div>
                    <div>
                        <label className="block text-xs font-medium text-gray-700 mb-1">Address</label>
                        <div className="flex gap-2">
                            <TypeaheadInput
                                onSelect={handleCreateAddressSelect}
                                placeholder="e.g. 23 Peters St, New York"
                                initialValue={editing ? editAddress : undefined}
                                onTextChange={editing ? setEditAddress : undefined}
                            />
                            <button
                                onClick={handleUseCurrentLocation}
                                className="px-3 py-2 text-sm text-amber-800 border border-amber-200 rounded hover:bg-amber-50 whitespace-nowrap"
                                title="Use my current location"
                            >
                                📍 Me
                            </button>
                        </div>
                    </div>
                    {/* Category picker. Visible in edit mode (always — a
                        normal edit operation) and in lockedToCreate (the Add
                        Place flow always saves Mine, so the user picks a
                        deliberate category to avoid landing at NULL). The
                        check-in composer's create flow stays quick — no
                        picker. */}
                    {(editing || categoryRequired) && (
                        <div>
                            <label className="block text-xs font-medium text-gray-700 mb-1">Category</label>
                            <CategoryTileGrid
                                categories={categories}
                                value={editCategoryId}
                                onChange={setEditCategoryId}
                                // Add Place flow forbids "None": rows have
                                // to land with a real category (use Other
                                // as the escape hatch). Edit mode still
                                // exposes None so a user can clear an
                                // existing category.
                                hideNone={categoryRequired}
                            />
                            {categoryRequired && editCategoryId == null && (
                                <p className="mt-1 text-xs text-gray-500">
                                    Pick a category to continue.
                                </p>
                            )}
                        </div>
                    )}
                    {/* Freeform notes — edit mode only. Context that tells
                        similar places apart in the grid (e.g. the event or
                        festival a place belongs to). */}
                    {editing && (
                        <div>
                            <label className="block text-xs font-medium text-gray-700 mb-1">Notes</label>
                            <textarea
                                value={editNotes}
                                onChange={(e) => setEditNotes(e.target.value)}
                                rows={2}
                                placeholder="e.g. Coachella 2026"
                                className="w-full px-3 py-2 text-sm border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-action resize-none"
                            />
                        </div>
                    )}
                </div>
            )}
            {mode === 'pin' && (
                // Pin tab: same map plumbing as 'create' (click-to-drop,
                // drag-to-move) but no name/address inputs. The 📍 Me button
                // recenters the map without dropping a pin — same as in
                // 'search' mode.
                <div className="px-4 py-3">
                    <div className="flex justify-end">
                        <button
                            onClick={handleUseCurrentLocation}
                            className="px-3 py-2 text-sm text-amber-800 border border-amber-200 rounded hover:bg-amber-50 whitespace-nowrap"
                            title="Use my current location"
                        >
                            📍 Me
                        </button>
                    </div>
                </div>
            )}

            <div className="flex-1 relative">
                <Map
                    style={{ width: '100%', height: '100%' }}
                    defaultCenter={initialView}
                    defaultZoom={initialView.zoom}
                    mapId={PICKER_MAP_ID}
                    gestureHandling="greedy"
                    disableDefaultUI={false}
                    onClick={handleMapClick}
                    onCameraChanged={handleCameraChanged}
                >
                    <MapController
                        pendingRecenter={pendingRecenter}
                        onApplied={() => setPendingRecenter(null)}
                    />
                    {markerPosition && (
                        <AdvancedMarker
                            position={markerPosition}
                            draggable={mode === 'create' || mode === 'pin'}
                            onDragEnd={(e) => {
                                const ll = e.latLng
                                if (ll) handleMarkerDrag(ll.lat(), ll.lng())
                            }}
                        >
                            <StandardPin />
                        </AdvancedMarker>
                    )}
                </Map>
            </div>

            <div className="px-4 py-3 border-t border-gray-200 flex items-center justify-between">
                <div className="text-xs text-gray-500 flex-1 min-w-0 pr-3 truncate">
                    {statusText}
                </div>
                <div className="flex gap-2 shrink-0">
                    <button
                        onClick={onClose}
                        className="px-3 py-1.5 text-sm text-gray-600 hover:text-gray-800"
                    >
                        Cancel
                    </button>
                    <button
                        disabled={!canConfirm || confirming}
                        onClick={handleConfirm}
                        className="px-3 py-1.5 text-sm bg-action text-plum rounded hover:bg-action-dark disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        {confirming ? 'Saving…' : confirmLabel}
                    </button>
                </div>
            </div>
        </>
    )
}

/**
 * Applies pending programmatic recentering via the imperative Map API.
 * Lives inside <Map> so it can call useMap(); does not render anything.
 * Map is uncontrolled (defaultCenter/defaultZoom) — this is the only path
 * that moves the camera from React.
 */
const MapController: React.FC<{
    pendingRecenter: { lat: number; lng: number; zoom: number } | null
    onApplied: () => void
}> = ({ pendingRecenter, onApplied }) => {
    const map = useMap()
    useEffect(() => {
        if (!map || !pendingRecenter) return
        map.panTo({ lat: pendingRecenter.lat, lng: pendingRecenter.lng })
        map.setZoom(pendingRecenter.zoom)
        onApplied()
    }, [map, pendingRecenter, onApplied])
    return null
}

/** Single-row collapsible category picker.
 *
 *  Collapsed (default): a one-row trigger showing the currently-selected
 *  category as an icon + name, with a chevron. Keeps the modal layout
 *  fixed-height so the map stays visible.
 *
 *  Expanded: a 4-col tile grid drops down as an absolute-positioned panel
 *  over the map. Picking a tile (or clicking outside) collapses again.
 *  Slug-keyed icons so the mapping survives any backend rename short of
 *  changing slugs. */
const CategoryTileGrid: React.FC<{
    categories: { id: number; name: string; slug: string }[]
    value: number | null
    onChange: (id: number | null) => void
    /** Hide the "None" / clear-selection tile. Used by the Add Place
     *  flow where "Uncategorized" isn't a valid landing state — pick
     *  "Other" instead. Edit mode leaves it on so an existing category
     *  can still be cleared. */
    hideNone?: boolean
}> = ({ categories, value, onChange, hideNone }) => {
    const [open, setOpen] = useState(false)
    const wrapperRef = useRef<HTMLDivElement>(null)

    // Click-outside-to-close. Bound only while open so we don't pay the
    // listener cost otherwise. Using mousedown matches the picker modal's
    // backdrop handling, so the backdrop doesn't swallow the dismiss.
    useEffect(() => {
        if (!open) return
        const handler = (e: MouseEvent) => {
            if (!wrapperRef.current?.contains(e.target as Node)) setOpen(false)
        }
        document.addEventListener('mousedown', handler)
        return () => document.removeEventListener('mousedown', handler)
    }, [open])

    const selected = value != null ? categories.find((c) => c.id === value) : undefined
    const SelectedIcon = selected ? (CATEGORY_ICON[selected.slug] ?? Tag) : null

    const tileClass = (sel: boolean) =>
        `flex flex-col items-center justify-center gap-1 px-2 py-2 rounded border text-xs transition-colors ${sel
            ? 'border-amber-500 bg-amber-50 text-amber-900'
            : 'border-gray-200 bg-white text-gray-700 hover:border-gray-300 hover:bg-gray-50'
        }`

    const pick = (id: number | null) => {
        onChange(id)
        setOpen(false)
    }

    return (
        <div ref={wrapperRef} className="relative">
            <button
                type="button"
                onClick={() => setOpen((o) => !o)}
                className="w-full flex items-center justify-between px-3 py-2 text-sm border border-gray-300 rounded hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-action bg-white"
                aria-haspopup="grid"
                aria-expanded={open}
            >
                <span className="flex items-center gap-2">
                    {SelectedIcon ? (
                        <SelectedIcon className="w-4 h-4 text-gray-600" />
                    ) : (
                        <span className="w-4 h-4 inline-flex items-center justify-center text-gray-400">—</span>
                    )}
                    <span className={selected ? 'text-gray-900' : 'text-gray-500'}>
                        {selected?.name ?? (hideNone ? 'Pick a category' : 'Uncategorized')}
                    </span>
                </span>
                <ChevronDown
                    className={`w-4 h-4 text-gray-500 transition-transform ${open ? 'rotate-180' : ''}`}
                />
            </button>
            {open && (
                <div
                    role="grid"
                    className="absolute left-0 right-0 top-full mt-1 z-20 bg-white border border-gray-200 rounded shadow-lg p-2"
                >
                    <div className="grid grid-cols-4 gap-2">
                        {!hideNone && (
                            <button
                                type="button"
                                onClick={() => pick(null)}
                                className={tileClass(value == null)}
                                aria-pressed={value == null}
                            >
                                <span className="w-5 h-5 flex items-center justify-center text-gray-400">—</span>
                                <span>None</span>
                            </button>
                        )}
                        {categories.map((c) => {
                            const Icon = CATEGORY_ICON[c.slug] ?? Tag
                            const sel = value === c.id
                            // hideNone forbids landing back at null, so
                            // click-to-deselect would create a state the
                            // caller has banned. Make selected tiles inert
                            // when hideNone is on.
                            const handleClick = () => {
                                if (sel && hideNone) return
                                pick(sel ? null : c.id)
                            }
                            return (
                                <button
                                    key={c.id}
                                    type="button"
                                    onClick={handleClick}
                                    className={tileClass(sel)}
                                    aria-pressed={sel}
                                    title={c.name}
                                >
                                    <Icon className="w-5 h-5" />
                                    <span>{c.name}</span>
                                </button>
                            )
                        })}
                    </div>
                </div>
            )}
        </div>
    )
}

const TypeaheadInput: React.FC<{
    onSelect: (candidate: PlaceCandidate) => void
    placeholder?: string
    /** Initial textbox contents. Uncontrolled: re-renders with a different
     *  value won't update the input. Used by edit mode to seed the address
     *  field with the row's existing address. */
    initialValue?: string
    /** Fires on every change to the textbox contents — typing, picks, and
     *  programmatic clears. Lets the parent capture free-text edits that
     *  never trigger `onSelect`. */
    onTextChange?: (text: string) => void
}> = ({ onSelect, placeholder = 'Search for a place or address…', initialValue, onTextChange }) => {
    const [query, setQuery] = useState(initialValue ?? '')
    const [candidates, setCandidates] = useState<PlaceCandidate[]>([])
    const [loading, setLoading] = useState(false)
    const [open, setOpen] = useState(false)
    // -1 means "no row highlighted yet"; Enter still selects index 0 in
    // that state so a quick type+Enter works without arrow-keying first.
    const [highlightedIndex, setHighlightedIndex] = useState(-1)
    const debounceRef = useRef<number | null>(null)
    const listRef = useRef<HTMLDivElement>(null)

    useEffect(() => {
        if (debounceRef.current) {
            window.clearTimeout(debounceRef.current)
            debounceRef.current = null
        }
        const trimmed = query.trim()
        if (trimmed.length < TYPEAHEAD_MIN_CHARS) {
            setCandidates([])
            setLoading(false)
            return
        }
        setLoading(true)
        debounceRef.current = window.setTimeout(async () => {
            try {
                const resp = await api.get('/api/places/resolve_search', { params: { q: trimmed } })
                setCandidates(resp.data?.candidates ?? [])
                setHighlightedIndex(-1)
            } catch {
                setCandidates([])
            } finally {
                setLoading(false)
            }
        }, TYPEAHEAD_DEBOUNCE_MS)
        return () => {
            if (debounceRef.current) {
                window.clearTimeout(debounceRef.current)
                debounceRef.current = null
            }
        }
    }, [query])

    // Keep the highlighted row visible when arrowing past the visible window.
    useEffect(() => {
        if (highlightedIndex < 0 || !listRef.current) return
        const child = listRef.current.children[highlightedIndex] as HTMLElement | undefined
        child?.scrollIntoView({ block: 'nearest' })
    }, [highlightedIndex])

    const select = (c: PlaceCandidate) => {
        onSelect(c)
        const next = c.display_name ?? ''
        setQuery(next)
        onTextChange?.(next)
        setOpen(false)
        setHighlightedIndex(-1)
    }

    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'ArrowDown') {
            if (candidates.length === 0) return
            e.preventDefault()
            setOpen(true)
            setHighlightedIndex((idx) => (idx + 1) % candidates.length)
        } else if (e.key === 'ArrowUp') {
            if (candidates.length === 0) return
            e.preventDefault()
            setOpen(true)
            setHighlightedIndex((idx) => (idx <= 0 ? candidates.length - 1 : idx - 1))
        } else if (e.key === 'Enter') {
            if (!open || candidates.length === 0) return
            e.preventDefault()
            const idx = highlightedIndex >= 0 ? highlightedIndex : 0
            select(candidates[idx])
        } else if (e.key === 'Escape') {
            if (!open) return
            // Don't bubble — the picker modal would otherwise close on Esc
            // when all the user wanted was to dismiss the suggestions.
            e.stopPropagation()
            setOpen(false)
            setHighlightedIndex(-1)
        }
    }

    return (
        <div className="flex-1 relative">
            <input
                type="text"
                value={query}
                onChange={(e) => {
                    setQuery(e.target.value)
                    onTextChange?.(e.target.value)
                    setOpen(true)
                }}
                onFocus={() => setOpen(true)}
                onBlur={() => setTimeout(() => setOpen(false), 150)}
                onKeyDown={handleKeyDown}
                placeholder={placeholder}
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-action"
                role="combobox"
                aria-expanded={open}
                aria-autocomplete="list"
                aria-activedescendant={
                    highlightedIndex >= 0 && candidates[highlightedIndex]
                        ? `typeahead-opt-${candidateKey(candidates[highlightedIndex])}`
                        : undefined
                }
            />
            {open && (loading || candidates.length > 0) && (
                <div
                    ref={listRef}
                    role="listbox"
                    className="absolute left-0 right-0 mt-1 bg-white border border-gray-200 rounded shadow-lg max-h-64 overflow-y-auto z-10"
                >
                    {loading && candidates.length === 0 && (
                        <div className="px-3 py-2 text-xs text-gray-500">Searching…</div>
                    )}
                    {candidates.map((c, i) => {
                        const isHighlighted = i === highlightedIndex
                        const key = candidateKey(c)
                        return (
                            <button
                                key={key}
                                id={`typeahead-opt-${key}`}
                                role="option"
                                aria-selected={isHighlighted}
                                onMouseDown={(e) => { e.preventDefault(); select(c) }}
                                onMouseEnter={() => setHighlightedIndex(i)}
                                className={`w-full text-left px-3 py-2 text-sm border-b border-gray-100 last:border-b-0 ${
                                    isHighlighted ? 'bg-amber-50' : 'hover:bg-gray-50'
                                }`}
                            >
                                <div className="flex items-center gap-2">
                                    <div className="font-medium text-gray-900 flex-1 min-w-0 truncate">
                                        {c.display_name}
                                    </div>
                                    {c.kind === 'private' && (
                                        <span className="shrink-0 text-[10px] uppercase tracking-wide text-amber-900 bg-amber-100 px-1.5 py-0.5 rounded">
                                            Saved
                                        </span>
                                    )}
                                </div>
                                {c.formatted_address && (
                                    <div className="text-xs text-gray-500">{c.formatted_address}</div>
                                )}
                            </button>
                        )
                    })}
                </div>
            )}
        </div>
    )
}

/** Stable React key / DOM id suffix for a candidate. Private rows always
 *  have a collection_item_id; Google rows always have a google_place_id;
 *  the prefix prevents collisions when both happen to point at the same
 *  numeric value space. */
function candidateKey(c: PlaceCandidate): string {
    if (c.kind === 'private') return `priv-${c.collection_item_id}`
    return `goog-${c.google_place_id}`
}

async function tryDeviceGeolocation(): Promise<{ lat: number; lng: number } | null> {
    if (typeof navigator === 'undefined' || !navigator.geolocation) return null
    return new Promise((resolve) => {
        const timeoutId = window.setTimeout(() => resolve(null), DEVICE_GEO_TIMEOUT_MS)
        navigator.geolocation.getCurrentPosition(
            (pos) => {
                window.clearTimeout(timeoutId)
                // The user asked for their location explicitly, so this fix is
                // free — no extra prompt — and warming the server cache with it
                // means the next check-in parse doesn't have to ask at all.
                void putDeviceLocation(
                    pos.coords.latitude,
                    pos.coords.longitude,
                    pos.coords.accuracy != null ? Math.round(pos.coords.accuracy) : undefined,
                ).catch(() => { /* map centring must not depend on this */ })
                resolve({ lat: pos.coords.latitude, lng: pos.coords.longitude })
            },
            () => {
                window.clearTimeout(timeoutId)
                resolve(null)
            },
            { enableHighAccuracy: false, timeout: DEVICE_GEO_TIMEOUT_MS, maximumAge: 60_000 },
        )
    })
}

export default LocationPickerModal
